<template>
    <div
        v-if="model !== false && timeline != null"
        class="border-b border-grey-500"
        :class="{
            'bg-grey-300/50': entry.pinned,
        }"
    >
        <div class="flex justify-between hover:cursor-pointer" @click="open = !open">
            <div class="pl-0 lg:pl-4 p-4 pr-0" v-if="page.user.labels.timeline.icon">
                <component :is="timeline.icon" />
            </div>

            <div
                class="py-2 overflow-hidden w-full flex flex-col justify-center"
                :class="{
                    'pl-3': page.user.labels.timeline.icon,
                    'pl-4': !page.user.labels.timeline.icon,
                }"
            >
                <p class="text-14 font-semibold" v-if="page.user.labels.timeline.title">
                    {{ timeline.title }}
                </p>
                <p class="text-14 block text-grey-900 truncate" v-if="page.user.labels.timeline.description">
                    {{ timeline.description }}
                </p>
            </div>

            <div class="pl-8 flex items-center">
                <p
                    class="text-grey-900 text-14"
                    v-tippy="{
                        placement: 'right',
                        content: $moment(entry.created_at).format('HH:mm'),
                    }"
                    v-if="page.user.labels.timeline.date"
                >
                    <!-- {{ timeline }} -->
                    {{ $moment(entry.history_at).format('DD/MM/YY') }}
                </p>

                <button class="pr-0 lg:pr-4 p-4 pl-3">
                    <IconArrowDown class="w-4.5 h-4.5 stroke-grey-900 transition-transform" :class="{ 'rotate-180': open }" />
                </button>
            </div>
        </div>

        <TransitionHeight :visable="open">
            <div
                v-if="timeline.breakdown != undefined"
                class="pr-10 lg:pr-14 gap-y-2 flex flex-col py-4 border-t border-grey-500 relative"
                :class="{
                    'ml-10 lg:ml-14': page.user.labels.timeline.icon,
                    'ml-4 lg:ml-4 ': !page.user.labels.timeline.icon,
                }"
            >
                <template v-for="(value, key) in timeline.breakdown">
                    <div v-if="value != null">
                        <p class="text-13 text-grey-900">
                            {{ $help.capitalizeFirstLetter(key) }}
                        </p>
                        <p class="text-14 whitespace-pre-line" v-if="typeof value == 'string'" v-html='value' />

                        <template v-else>
                            <Call
                                v-if="key == 'Lead recording' || key == 'Call recording'"
                                :call="value"
                                class="mt-[6px]"
                                :showTitle="false"
                                :showDescription="false"
                            />

                            <NuxtLink
                                class="link-text text-14 text-purple-600"
                                v-if="value.type == 'NuxtLink'"
                                :to="value.to"
                                v-text="value.text"
                            />

                            <template v-if="typeof value == 'object' && value.type == 'status'">
                                <div class="flex items-center">
                                    <div
                                        v-if="value.circleClass || value.circleStyle"
                                        class="w-2 h-2 rounded-full"
                                        :class="[value.circleClass]"
                                        :style="{
                                            backgroundColor: value.circleStyle,
                                        }"
                                    />
                                    <component class="w-4 h-4" v-if="value.icon" :is="value.icon" />
                                    <p class="pl-1.5 text-14">
                                        {{ value.text }}
                                    </p>
                                </div>
                            </template>
                            <template v-if="typeof value == 'object' && value.type == 'dealStatus'">
                                <DropdownV2 class="mt-1" :menuSpace="8">
                                    <template #button="{ open }" }>
                                        <div class="w-2 h-2 rounded-full mr-2 shrink-0" :style="{ backgroundColor: value.circleStyle }" />
                                        <p class="text-14 capitalize">
                                            {{ value.text }}
                                        </p>
                                        <IconArrowSmallDown class="stroke-grey-900" />
                                    </template>

                                    <template #menu>
                                        <div class="table-multi-select-menu bg-black w-[184px]">
                                            <p class="text-grey-800 text-14 pb-1.5 px-4 pt-0.5">Change stage</p>

                                            <template v-for="stage in user.deal_stages">
                                                <button
                                                    :disabled="value.deal.status == stage.id"
                                                    data-close
                                                    @click="value.deal.updateStatus(stage.id)"
                                                >
                                                    <div
                                                        class="w-2 h-2 rounded-full"
                                                        :style="{
                                                            'background-color':
                                                                value.deal.status == stage.id
                                                                    ? '#7C7C7C'
                                                                    : user.pipelineStage(stage.id).color,
                                                        }"
                                                    />
                                                    <p class="pl-2">{{ $help.capitalizeFirstLetter(user.pipelineStage(stage.id).name) }}</p>
                                                </button>
                                            </template>

                                            <button data-close @click="value.deal.updateStatus(0)" v-if="value.deal.status != 0">
                                                <div class="w-2 h-2 rounded-full bg-green-500" />
                                                <p class="pl-2">Won</p>
                                            </button>
                                            <button data-close @click="value.deal.updateStatus('lost')" v-if="value.deal.lost == null">
                                                <div class="w-2 h-2 rounded-full bg-red-700" />
                                                <p class="pl-2">Lost</p>
                                            </button>
                                        </div>
                                    </template>
                                </DropdownV2>
                            </template>

                            <template v-if="typeof value == 'object' && value.type == 'statusMenu'">
                                <DropdownV2 buttonCSS="items-center text-black" :menuSpace="8">
                                    <template #button="{ open }" }>
                                        <div class="flex items-center mt-0.5">
                                            <!-- <component class="w-4 h-4" :is="event.statusIconName()" /> -->
                                            <div class="w-2 h-2 rounded-full" :class="[value.circleClass]" />
                                            <p class="text-14 capitalize pl-1.5">{{ value.text }}</p>
                                        </div>
                                        <IconArrowSmallDown class="stroke-grey-900" />
                                    </template>

                                    <template #menu>
                                        <div class="table-multi-select-menu bg-black min-w-[210px]">
                                            <p class="text-grey-800 text-14 pb-1.5 px-4 pt-0.5">Event options</p>
                                            <button data-close @click="value.event.complete()" v-if="value.event.appointment == 0">
                                                Mark as done
                                            </button>
                                            <button data-close @click="value.event.edit()">Reschedule</button>
                                            <button data-close @click="value.event.complete()" v-if="value.event.appointment == 1">
                                                Cancel event
                                            </button>
                                        </div>
                                    </template>
                                </DropdownV2>
                            </template>

                            <template v-if="value.type == 'Upload'">
                                <EmailAttachment :upload="upload" v-for="upload in value.uploads" />

                                <template v-if="value.uploads.length == 0">
                                    No attachments
                                </template>
                            </template>

                            <template v-if="typeof value == 'object' && value.type == 'tail'">
                                <p
                                    class="text-14"
                                    :class="{
                                        'line-through': timeline.noLineKeys.indexOf(key) == -1,
                                    }"
                                    v-if="value.before != null"
                                >
                                    {{ value.before }}
                                </p>
                                <p class="text-14">
                                    {{ value.after }}
                                </p>
                            </template>

                            <template v-if="typeof value == 'object' && value.type == undefined">
                                <p
                                    class="text-14"
                                    :class="{
                                        'line-through': timeline.noLineKeys.indexOf(key) == -1,
                                    }"
                                    v-if="value.before != null"
                                >
                                    {{ value.before }}
                                </p>
                                <p class="text-14">
                                    {{ value.after }}
                                </p>
                            </template>
                        </template>
                    </div>
                </template>

                <div class="w-4.5 h-4.5 absolute right-4 bottom-4" v-if="timeline.options != undefined">
                    <DropdownV2>
                        <template #button>
                            <IconAdditionalSettings class="w-4.5 h-4.5 icon-hover-grey fill-grey-800 hover:fill-grey-900" />
                        </template>

                        <template #menu>
                            <div class="table-multi-select-menu bg-black min-w-[200px]">
                                <template v-for="(func, name) in timeline.options">
                                    <button v-if="func != null" @click="func()" data-close>
                                        {{ name }}
                                    </button>
                                </template>

                                <button @click="pin" data-close>{{ entry.pinned ? 'Unpin' : 'Pin' }}</button>
                            </div>
                        </template>
                    </DropdownV2>
                </div>
            </div>
        </TransitionHeight>

        <!-- <pre>
            {{ entry }}
        </pre> -->
    </div>
</template>

<script>
import Note from '~/models/note.js'
import Task from '~/models/task.js'
import Appointment from '~/models/appointment.js'
import SMS from '~/models/sms.js'
import Email from '~/models/email.js'
import Deal from '~/models/deal.js'
import Lead from '~/models/lead.js'
import CallModel from '~/models/call.js'
import Log from '~/models/log.js'
import Upload from '~/models/upload.js'
import EmailAttachment from '~/components/customer/blocks/email-attachment.vue'
import EventEditMenu from '~/components/customer/blocks/event-edit-menu.vue'
import Entry from '~/models/entry.js'

export default {
    props: {
        entry: Object,
        origin: Object,
        page: Object,
        modelCreated: String,
    },

    data() {
        return {
            open: false,
        }
    },

    computed: {
        user() {
            return this.$authSanctum.getUser()
        },

        model() {
            let className = {
                // 46: Setting,
                // 48: Communication,
                100: Lead,
                // 102: Contact,
                103: Deal,
                201: SMS,
                202: Note,
                203: Email,
                204: Upload,
                205: Appointment,
                206: CallModel,
                207: Task,
            }[parseInt(this.entry.model_type)]

            if (className == undefined) {
                return new Log(this.entry.historial, {
                    vue: this,
                    $help: this.$help,
                    modelType: this.entry.model_type,
                })
            }

            return new className(this.entry.historial, {
                vue: this,
                timelineOrigin: this.origin,
                modelCreated: this.modelCreated,
                $help: this.$help,
                entry: new Entry(this.entry, {
                    $help: this.$help,
                    user: this.user,
                    pageUser: this.origin.user != undefined ? this.origin.user : null
                })
            })
        },

        timeline() {
            return this.model.timeline(this.user)
        },

        pin() {
            this.$axios
                .post('/history/' + this.entry.id + '/pin', {
                    pin: !this.entry.pinned,
                })
                .then((res) => {
                    this.$event.$emit('timeline-pinned')
                })
        },
    },

    components: {
        EmailAttachment,
        EventEditMenu,
    },
}
</script>
